import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaMapMarkerAlt, FaUsers } from 'react-icons/fa';
import '../styles/GujaratPage.css'; 
import ClientBranches from './ClientBranches';
import CompanyProfile1 from './CompanyProfile1';

const GujaratClientsPage = () => {
  const [visibleCards, setVisibleCards] = useState(0);
  const [counters, setCounters] = useState({
    clients: 0,
    peopleServed: 0,
    locations: 0
  });

  // Refs for Intersection Observer to animate hero text
  const gcpHeroTitleRef = useRef(null);
  const gcpHeroBreadcrumbRef = useRef(null);

  // Gujarat specific client data
  const gujaratClients = [
    { name: "SUZLON ENERGY LTD.", location: "Bhuj", persons: 1200, logo: "/clients/Suzlon-3.png", industry: "Energy" },
    { name: "EVEREST INDIA INDUSTRIES PVT. LTD.", location: "Dahej", persons: 800, logo: "/clients/Everest-industries-4.png", industry: "Industrial" },
    { name: "NIRMAL POLY PLAST PVT. LTD", location: "Angam (Unit-1), Gujarat", persons: 500, logo: "/newlyaddedclients/Nirmal-Poly-Plastic.png", industry: "Manufacturing industry" },
    { name: "PAPRO INDUSTRIES", location: "Sanjan, Gujarat", persons: 200, logo: "/newlyaddedclients/Papro-industries.png", industry: "Industrial" }
  ];

  // Calculate total people served and unique locations for Gujarat
  const totalPeopleServed = gujaratClients.reduce((sum, client) => sum + client.persons, 0);
  const totalLocations = [...new Set(gujaratClients.map(client => client.location))].length;

  // Animate counters on component mount
  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          clients: Math.floor(gujaratClients.length * progress),
          peopleServed: Math.floor(totalPeopleServed * progress),
          locations: Math.floor(totalLocations * progress)
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          setCounters({
            clients: gujaratClients.length,
            peopleServed: totalPeopleServed,
            locations: totalLocations
          });
        }
      }, stepTime);
    };
    animateCounters();
  }, [gujaratClients.length, totalPeopleServed, totalLocations]);

  // Animate client cards appearing in a staggered manner
  useEffect(() => {
    const timer = setTimeout(() => {
      if (visibleCards < gujaratClients.length) {
        setVisibleCards(prev => prev + 1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [visibleCards, gujaratClients.length]);

  // Callback for Intersection Observer to add/remove animation class
  const handleIntersect = useCallback((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("gcp-animate-in");
      } else {
        entry.target.classList.remove("gcp-animate-in");
      }
    });
  }, []);

  // Set up Intersection Observers for hero text elements
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2,
    };
    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    const currentHeroTitleRef = gcpHeroTitleRef.current;
    const currentHeroBreadcrumbRef = gcpHeroBreadcrumbRef.current;

    if (currentHeroTitleRef) observer.observe(currentHeroTitleRef);
    if (currentHeroBreadcrumbRef) observer.observe(currentHeroBreadcrumbRef);

    return () => {
      if (currentHeroTitleRef) observer.unobserve(currentHeroTitleRef);
      if (currentHeroBreadcrumbRef) observer.unobserve(currentHeroBreadcrumbRef);
    };
  }, [handleIntersect]);

  return (
    <>
    <Helmet>
      <title>Clients in Gujarat | Trusted Industrial Partners | Laxmi Hospitality Services</title>
      <meta
        name="description"
        content="Discover Laxmi Hospitality Services valued clients in Gujarat including Suzlon Energy Ltd. in Bhuj and Everest India Industries Pvt. Ltd. in Dahej. Serving diverse industries with trusted, scalable catering solutions empowering thousands daily."
      />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link rel="canonical" href="https://laxmihospitalityservices.in/clients/gujarat" />
    
      {/* -------- OPEN GRAPH (SOCIAL) -------- */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://laxmihospitalityservices.in/clients/gujarat" />
      <meta property="og:title" content="Clients in Gujarat | Trusted Industrial Partners | Laxmi Hospitality Services" />
      <meta property="og:description" content="Discover our strategic partnerships in Gujarat. Serving 2 leading companies across 2 locations with 2,000+ people served daily." />
      <meta property="og:site_name" content="Laxmi Hospitality Services" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:image" content="https://laxmihospitalityservices.in/images/gujarat.png" />
    
      {/* -------- GUJARAT CLIENTS COLLECTION PAGE -------- */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": "https://laxmihospitalityservices.in/clients/gujarat",
            "name": "Gujarat Clients - Laxmi Hospitality Services",
            "description": "Industrial catering clients across Gujarat state - serving leading companies in energy and industrial manufacturing sectors.",
            "url": "https://laxmihospitalityservices.in/clients/gujarat",
            "about": { "@id": "https://laxmihospitalityservices.in/#organization" },
            "publisher": { "@id": "https://laxmihospitalityservices.in/#organization" },
            "isPartOf": {
              "@type": "WebPage",
              "@id": "https://laxmihospitalityservices.in/clients",
              "name": "Our Clients Across India"
            },
            "mainEntity": {
              "@type": "ItemList",
              "name": "Gujarat Industrial Clients",
              "description": "Leading industrial companies served by Laxmi Hospitality Services across Gujarat",
              "numberOfItems": 2,
              "itemListElement": [
                {
                  "@type": "Organization",
                  "name": "Suzlon Energy Ltd.",
                  "description": "Renewable energy leader in Bhuj serving 1,200+ people",
                  "address": {
                    "@type": "PostalAddress",
                    "addressLocality": "Bhuj",
                    "addressRegion": "Gujarat",
                    "addressCountry": "IN"
                  }
                },
                {
                  "@type": "Organization",
                  "name": "Everest India Industries Pvt. Ltd.",
                  "description": "Industrial manufacturing excellence in Dahej serving 800+ people",
                  "address": {
                    "@type": "PostalAddress",
                    "addressLocality": "Dahej",
                    "addressRegion": "Gujarat",
                    "addressCountry": "IN"
                  }
                }
              ]
            },
            "sameAs": [
              "https://www.linkedin.com/company/laxmi-hospitality-services/",
              "https://www.instagram.com/laxmi_hospitality_services/"
            ],
            "inLanguage": "en-IN"
          }
        `}
      </script>
    
      {/* -------- STATE-SPECIFIC BREADCRUMB -------- */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": "https://laxmihospitalityservices.in/#breadcrumb",
            "name": "Website Breadcrumb",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home",    "item": "https://laxmihospitalityservices.in/" },
              { "@type": "ListItem", "position": 2, "name": "Clients", "item": "https://laxmihospitalityservices.in/clients" },
              { "@type": "ListItem", "position": 3, "name": "Gujarat", "item": "https://laxmihospitalityservices.in/clients/gujarat" }
            ]
          }
        `}
      </script>
</Helmet>

    <div className="gcp-page-container">
      {/* Hero Section */}
      <section className="gcp-hero-section">
        <img
          src="/images/gujarat.png" 
          alt="Gujarat Industry Landscape"
          className="gcp-hero-image"
          onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/1920x600/f0f0f0/333333?text=Gujarat+Hero"; }}
        />
        <div className="gcp-hero-overlay">
          <h2 ref={gcpHeroTitleRef} className="gcp-hero-title">Our Clients in Gujarat</h2>
          <p ref={gcpHeroBreadcrumbRef} className="gcp-breadcrumb">
            <a href="/" className="gcp-breadcrumb-link">Home</a> /
            <a href="/clients" className="gcp-breadcrumb-link"> Clients</a> / Gujarat
          </p>
        </div>
      </section>

      {/* Introductory Sentence for Stats Section */}
      <section className="gcp-intro-stats-section">
        <div className="gcp-container">
          <p className="gcp-intro-stats-text">
            In the dynamic state of Gujarat, our partnerships span across its thriving industrial and commercial sectors, delivering exceptional service and fostering growth for countless businesses and individuals.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="gcp-stats-section">
        <div className="gcp-container">
          <div className="gcp-stats-grid">
            <div className="gcp-stat-card">
              <div className="gcp-stat-number">{counters.clients}</div>
              <div className="gcp-stat-label">Clients</div>
            </div>
            <div className="gcp-stat-card">
              <div className="gcp-stat-number">{counters.peopleServed.toLocaleString()}</div>
              <div className="gcp-stat-label">People Serving</div>
            </div>
            <div className="gcp-stat-card">
              <div className="gcp-stat-number">{counters.locations}</div>
              <div className="gcp-stat-label">Locations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="gcp-clients-section">
        <div className="gcp-container">
          <h2 className="gcp-clients-section-title">Our Valued Clients</h2>
          <p className="gcp-clients-section-intro">
            Explore our partnerships with leading companies in Gujarat, driving innovation and success across various sectors.
          </p>
          <div className="gcp-clients-grid">
            {gujaratClients
              .slice()
              .sort((a, b) => b.persons - a.persons)
              .map((client, index) => (
              <div
                key={index}
                className={`gcp-client-card ${index < visibleCards ? 'gcp-visible' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Client Logo at the Top - Prominent */}
                <div className="gcp-card-logo-wrapper">
                  <img
                    src={client.logo}
                    alt={`${client.name} Logo`}
                    className="gcp-card-logo-image"
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/150x100/e0e0e0/333333?text=${client.name.split(' ')[0]}`; }}
                  />
                </div>

                {/* Client Info below logo */}
                <div className="gcp-client-info-v2">
                  <h3 className="gcp-client-name-v2">{client.name}</h3>
                  <div className="gcp-location-v2">
                    <FaMapMarkerAlt className="gcp-icon-v2" />
                    {client.location}
                  </div>
                  <div className="gcp-people-served">
                    <FaUsers className="gcp-icon-v2" />
                    {client.persons.toLocaleString()} People Serving
                  </div>
                  <div className="gcp-type-tag">
                    Type: {client.industry}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <ClientBranches/>
      <CompanyProfile1/>
    </div>
    </>
  );
};

export default GujaratClientsPage;
